/**
 * Azure AD (Entra ID) authentication configuration for MSAL.
 * Use env vars only to enable/disable each login; auth status is read from storage/cookies/MSAL.
 */

import {
  type Configuration,
  LogLevel,
  PublicClientApplication,
} from "@azure/msal-browser";
import { getEnv } from "../config/getEnv";

export type AzureEnv = {
  clientId: string;
  tenantId: string;
  redirectUri: string;
  scopes: string[];
  postLogoutRedirectUri?: string;
  cacheLocation?: "sessionStorage" | "localStorage";
};

const DEFAULT_SCOPES = ["User.Read", "openid", "profile"];

/** Whether Azure login is enabled (env only). When false, skip Azure authentication. */
export function isAzureLoginEnabled(): boolean {
  const v = (getEnv("REACT_APP_AZURE_LOGIN_ENABLED") as string) ?? "true";
  return v.toLowerCase() === "false" ? false : true;
}

/** Whether Salesforce login is enabled (env only). When false, skip Salesforce authentication. */
export function isSfLoginEnabled(): boolean {
  const v = (getEnv("REACT_APP_SF_LOGIN_ENABLED") as string) ?? "true";
  return v.toLowerCase() === "true";
}

export function readAzureEnv(): AzureEnv | null {
  if (!isAzureLoginEnabled()) return null;

  const clientId = getEnv("REACT_APP_AZURE_CLIENT_ID") as string | undefined;
  const tenantId = getEnv("REACT_APP_AZURE_TENANT_ID") as string | undefined;
  const redirectUri =
    (getEnv("REACT_APP_AZURE_REDIRECT_URI") as string | undefined) ||
    (typeof window !== "undefined"
      ? `${window.location.origin}/azure/callback`
      : "");
  const scopesStr =
    (getEnv("REACT_APP_AZURE_SCOPES") as string | undefined) ?? "";
  const scopes = scopesStr
    ? scopesStr
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
    : DEFAULT_SCOPES;
  const postLogoutRedirectUri =
    (getEnv("REACT_APP_AZURE_POST_LOGOUT_REDIRECT_URI") as
      | string
      | undefined) ||
    (typeof window !== "undefined" ? window.location.origin : undefined);
  const cacheLocation =
    (getEnv("REACT_APP_AZURE_CACHE_LOCATION") as
      | "sessionStorage"
      | "localStorage"
      | undefined) ?? "sessionStorage";

  if (!clientId || !tenantId || !redirectUri) return null;

  return {
    clientId,
    tenantId,
    redirectUri,
    scopes,
    postLogoutRedirectUri,
    cacheLocation,
  };
}

function buildMsalConfiguration(env: AzureEnv): Configuration {
  const authority = `https://login.microsoftonline.com/${env.tenantId}`;
  return {
    auth: {
      clientId: env.clientId,
      authority,
      redirectUri: env.redirectUri,
      postLogoutRedirectUri: env.postLogoutRedirectUri ?? env.redirectUri,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: env.cacheLocation ?? "sessionStorage",
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: (_level: LogLevel, message: string) => {
          if (process.env.NODE_ENV === "development") {
            console.debug("[MSAL]", message);
          }
        },
        logLevel:
          process.env.NODE_ENV === "development"
            ? LogLevel.Info
            : LogLevel.Error,
      },
    },
  };
}

let msalInstance: PublicClientApplication | null = null;

/**
 * Returns the MSAL PublicClientApplication instance for Azure AD.
 * Only created when Azure login is enabled and Azure env is valid.
 */
export function getMsalInstance(): PublicClientApplication | null {
  if (msalInstance) return msalInstance;
  const env = readAzureEnv();
  if (!env) return null;
  const config = buildMsalConfiguration(env);
  msalInstance = new PublicClientApplication(config);
  return msalInstance;
}

/**
 * Scopes to request at login (and for acquireTokenSilent).
 */
export function getAzureLoginScopes(): string[] {
  const env = readAzureEnv();
  return env?.scopes ?? DEFAULT_SCOPES;
}
